import { useMemo, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import { Clock3, Fingerprint, KeyRound, Laptop, QrCode, ShieldCheck, Smartphone, Trash2 } from "lucide-react";
import { trpc } from "@/lib/trpc";

function formatDate(value: Date | string | null) {
  if (!value) return "Not used yet";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Not used yet"
    : date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function friendlyError(error: unknown, fallback: string) {
  const message = error instanceof Error ? error.message : "";
  if (/notallowed|cancelled|canceled/i.test(message)) return "Passkey setup was cancelled. No changes were made.";
  return message || fallback;
}

export default function PasskeySecurityCard() {
  const utils = trpc.useUtils();
  const credentials = trpc.auth.passkey.listCredentials.useQuery(undefined, { retry: false });
  const registerOptions = trpc.auth.passkey.registerOptions.useMutation();
  const registerVerify = trpc.auth.passkey.registerVerify.useMutation();
  const revokeCredential = trpc.auth.passkey.revokeCredential.useMutation();
  const totpStatus = trpc.auth.totp.status.useQuery(undefined, { retry: false });
  const beginTotpEnrollment = trpc.auth.totp.beginEnrollment.useMutation();
  const confirmTotpEnrollment = trpc.auth.totp.confirmEnrollment.useMutation();
  const removeTotp = trpc.auth.totp.remove.useMutation();
  const [feedback, setFeedback] = useState<{ kind: "success" | "error"; message: string } | null>(null);
  const [totpEnrollment, setTotpEnrollment] = useState<{ enrollmentId: string; manualKey: string; qrDataUrl: string } | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [recoveryAcknowledged, setRecoveryAcknowledged] = useState(false);
  const [removalCode, setRemovalCode] = useState("");
  const supportsPasskeys = useMemo(
    () => typeof window !== "undefined" && "PublicKeyCredential" in window && window.isSecureContext,
    [],
  );
  const isEnrolling = registerOptions.isPending || registerVerify.isPending;
  const isSettingUpTotp = beginTotpEnrollment.isPending || confirmTotpEnrollment.isPending;

  async function enrollPasskey() {
    setFeedback(null);
    if (!supportsPasskeys) {
      setFeedback({
        kind: "error",
        message: "Passkeys require a current browser on a secure HTTPS connection. You can still use your email sign-in code.",
      });
      return;
    }

    try {
      const { ceremonyId, options } = await registerOptions.mutateAsync();
      const response = await startRegistration({ optionsJSON: options });
      await registerVerify.mutateAsync({ ceremonyId, response });
      await utils.auth.passkey.listCredentials.invalidate();
      setFeedback({ kind: "success", message: "Passkey added. You can now use your device’s secure sign-in prompt on this account." });
    } catch (error) {
      setFeedback({ kind: "error", message: friendlyError(error, "Your passkey could not be added. Try again or use your email sign-in code.") });
    }
  }

  async function removePasskey(credentialId: string) {
    setFeedback(null);
    try {
      await revokeCredential.mutateAsync({ credentialId });
      await utils.auth.passkey.listCredentials.invalidate();
      setFeedback({ kind: "success", message: "Passkey removed. Your email sign-in code remains available." });
    } catch (error) {
      setFeedback({ kind: "error", message: friendlyError(error, "This passkey could not be removed. Please try again.") });
    }
  }

  async function beginTotpSetup() {
    setFeedback(null);
    setRecoveryCodes(null);
    setRecoveryAcknowledged(false);
    try {
      const enrollment = await beginTotpEnrollment.mutateAsync();
      setTotpEnrollment(enrollment);
      setTotpCode("");
    } catch (error) {
      setFeedback({ kind: "error", message: friendlyError(error, "Authenticator-app setup could not be started. Please try again.") });
    }
  }

  async function confirmTotpSetup() {
    if (!totpEnrollment) return;
    const code = totpCode.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      setFeedback({ kind: "error", message: "Enter the current six-digit code from your authenticator app to finish setup." });
      return;
    }

    setFeedback(null);
    try {
      const result = await confirmTotpEnrollment.mutateAsync({ enrollmentId: totpEnrollment.enrollmentId, code });
      setTotpEnrollment(null);
      setTotpCode("");
      setRecoveryCodes(result.recoveryCodes);
      setRecoveryAcknowledged(false);
      await utils.auth.totp.status.invalidate();
      setFeedback({ kind: "success", message: "Authenticator app enabled. Save the recovery codes below before you leave this page." });
    } catch (error) {
      setFeedback({ kind: "error", message: friendlyError(error, "That authenticator code could not be verified. Try the current code and complete setup within five minutes.") });
    }
  }

  async function removeTotpFactor() {
    const code = removalCode.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      setFeedback({ kind: "error", message: "Enter a current six-digit authenticator code before removing this sign-in factor." });
      return;
    }

    setFeedback(null);
    try {
      await removeTotp.mutateAsync({ code });
      setRemovalCode("");
      setTotpEnrollment(null);
      setRecoveryCodes(null);
      await utils.auth.totp.status.invalidate();
      setFeedback({ kind: "success", message: "Authenticator app removed. Your email-code sign-in remains available." });
    } catch (error) {
      setFeedback({ kind: "error", message: friendlyError(error, "That authenticator code could not be verified. The authenticator app was not removed.") });
    }
  }

  return (
    <section
      aria-labelledby="passkey-security-title"
      style={{
        background: "transparent",
        border: 0,
        borderRadius: 0,
        padding: 0,
        boxShadow: "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", paddingBottom: "1.25rem", borderBottom: "1px solid rgba(255,255,255,.1)" }}>
        <div style={{ display: "flex", gap: ".85rem", maxWidth: 560 }}>
          <div aria-hidden="true" style={{ display: "grid", placeItems: "center", width: 42, height: 42, borderRadius: ".8rem", color: "#7DEAFF", background: "rgba(0,212,255,.1)", border: "1px solid rgba(0,212,255,.24)", flexShrink: 0 }}>
            <Fingerprint size={21} />
          </div>
          <div>
            <p style={{ margin: 0, color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".7rem", fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase" }}>
              Sign-in security
            </p>
            <h2 id="passkey-security-title" style={{ margin: ".25rem 0 .4rem", color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1.04rem", fontWeight: 700 }}>
              Passkeys on your devices
            </h2>
            <p style={{ margin: 0, color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>
              Use Face ID, Touch ID, a device PIN, or an approved security key instead of waiting for an email code. Your email code remains a recovery option.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void enrollPasskey()}
          disabled={isEnrolling || !supportsPasskeys}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: ".5rem",
            minHeight: 42,
            padding: ".68rem .95rem",
            border: 0,
            borderRadius: ".65rem",
            color: "#06121B",
            background: isEnrolling || !supportsPasskeys ? "rgba(125,234,255,.48)" : "linear-gradient(135deg,#7DEAFF,#46C6DF)",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: ".78rem",
            fontWeight: 750,
            cursor: isEnrolling || !supportsPasskeys ? "not-allowed" : "pointer",
          }}
        >
          <KeyRound size={15} aria-hidden="true" />
          {isEnrolling ? "Adding passkey…" : "Add a passkey"}
        </button>
      </div>

      {!supportsPasskeys && (
        <p role="status" style={{ margin: "1rem 0 0", padding: ".72rem .85rem", borderRadius: ".65rem", color: "#FDE68A", background: "rgba(245,166,35,.08)", border: "1px solid rgba(245,166,35,.2)", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
          This browser cannot add a passkey here. Try an updated browser over HTTPS, or continue with your email sign-in code.
        </p>
      )}

      {feedback && (
        <p role={feedback.kind === "error" ? "alert" : "status"} style={{ margin: "1rem 0 0", padding: ".72rem .85rem", borderRadius: ".65rem", color: feedback.kind === "error" ? "#FCA5A5" : "#A7F3D0", background: feedback.kind === "error" ? "rgba(244,63,94,.08)" : "rgba(16,185,129,.08)", border: `1px solid ${feedback.kind === "error" ? "rgba(244,63,94,.22)" : "rgba(16,185,129,.22)"}`, fontFamily: "'Inter', sans-serif", fontSize: ".78rem", lineHeight: 1.55 }}>
          {feedback.message}
        </p>
      )}

      <div style={{ marginTop: "1rem" }}>
        {credentials.isLoading && <p style={{ margin: "1rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: ".8rem" }}>Loading your passkeys…</p>}
        {credentials.isError && <p role="alert" style={{ margin: "1rem 0 0", color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: ".8rem" }}>Your passkeys could not be loaded. Refresh the page to try again.</p>}
        {credentials.data?.length === 0 && !credentials.isLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: ".65rem", marginTop: "1rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
            <ShieldCheck size={17} aria-hidden="true" style={{ color: "#7DEAFF", flexShrink: 0 }} />
            No passkeys are enrolled yet. Add one on a device you use regularly.
          </div>
        )}
        {credentials.data?.map((credential) => (
          <article key={credential.credentialId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", marginTop: ".85rem", paddingTop: ".9rem", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".72rem", minWidth: 0 }}>
              <div aria-hidden="true" style={{ display: "grid", placeItems: "center", width: 34, height: 34, borderRadius: ".6rem", color: "#C4B5FD", background: "rgba(168,85,247,.12)", flexShrink: 0 }}>
                <Laptop size={16} />
              </div>
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, color: "#F8FAFC", fontFamily: "'Inter', sans-serif", fontSize: ".82rem", fontWeight: 650, overflowWrap: "anywhere" }}>
                  {credential.nickname || (credential.deviceType === "multiDevice" ? "Synced passkey" : "Device-bound passkey")}
                </p>
                <p style={{ display: "flex", alignItems: "center", gap: ".35rem", margin: ".18rem 0 0", color: "#8491A5", fontFamily: "'Inter', sans-serif", fontSize: ".72rem" }}>
                  <Clock3 size={12} aria-hidden="true" /> Last used {formatDate(credential.lastUsedAt)} · {credential.backedUp ? "backup available" : "device only"}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void removePasskey(credential.credentialId)}
              disabled={revokeCredential.isPending}
              aria-label={`Remove ${credential.nickname || "passkey"}`}
              style={{ display: "inline-flex", alignItems: "center", gap: ".38rem", minHeight: 36, padding: ".45rem .65rem", borderRadius: ".55rem", border: "1px solid rgba(251,113,133,.28)", color: "#FDA4AF", background: "rgba(244,63,94,.04)", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".72rem", fontWeight: 650, cursor: revokeCredential.isPending ? "wait" : "pointer" }}
            >
              <Trash2 size={13} aria-hidden="true" /> Remove
            </button>
          </article>
        ))}
      </div>

      <section aria-labelledby="totp-security-title" style={{ marginTop: "1.35rem", paddingTop: "1.25rem", borderTop: "1px solid rgba(255,255,255,.1)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: ".9rem" }}>
          <div style={{ display: "flex", gap: ".72rem", maxWidth: 560 }}>
            <div aria-hidden="true" style={{ display: "grid", placeItems: "center", width: 38, height: 38, borderRadius: ".7rem", color: "#C4B5FD", background: "rgba(168,85,247,.12)", border: "1px solid rgba(196,181,253,.22)", flexShrink: 0 }}>
              <Smartphone size={18} />
            </div>
            <div>
              <p style={{ margin: 0, color: "#C4B5FD", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".7rem", fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase" }}>
                Authenticator app
              </p>
              <h3 id="totp-security-title" style={{ margin: ".22rem 0 .35rem", color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".98rem", fontWeight: 700 }}>
                Add a six-digit second factor
              </h3>
              <p style={{ margin: 0, color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
                Use an authenticator app such as 1Password, Google Authenticator, or Microsoft Authenticator after your email code or passkey. This is optional and adds a second sign-in check.
              </p>
            </div>
          </div>
          {!totpStatus.data?.enabled && !totpEnrollment && !recoveryCodes && (
            <button type="button" onClick={() => void beginTotpSetup()} disabled={isSettingUpTotp || totpStatus.isLoading} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: ".46rem", minHeight: 40, padding: ".62rem .85rem", borderRadius: ".6rem", border: "1px solid rgba(196,181,253,.35)", color: "#F3E8FF", background: isSettingUpTotp ? "rgba(168,85,247,.18)" : "linear-gradient(135deg, rgba(168,85,247,.38), rgba(124,58,237,.3))", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".76rem", fontWeight: 700, cursor: isSettingUpTotp || totpStatus.isLoading ? "wait" : "pointer" }}>
              <QrCode size={15} aria-hidden="true" /> {isSettingUpTotp ? "Preparing setup…" : "Set up authenticator app"}
            </button>
          )}
        </div>

        {totpStatus.isLoading && <p style={{ margin: ".9rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: ".78rem" }}>Checking authenticator-app status…</p>}
        {totpStatus.isError && <p role="alert" style={{ margin: ".9rem 0 0", color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: ".78rem" }}>Authenticator-app status could not be loaded. Refresh this page to try again.</p>}

        {totpEnrollment && (
          <form onSubmit={event => { event.preventDefault(); void confirmTotpSetup(); }} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: ".9rem", marginTop: "1rem", padding: "1rem", borderRadius: ".8rem", background: "rgba(168,85,247,.07)", border: "1px solid rgba(196,181,253,.24)" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: ".9rem", flexWrap: "wrap" }}>
              <img src={totpEnrollment.qrDataUrl} alt="Scan this QR code with your authenticator app" width={144} height={144} style={{ width: 144, height: 144, padding: 7, borderRadius: ".6rem", background: "#FFF" }} />
              <div style={{ minWidth: 0, flex: "1 1 220px" }}>
                <p style={{ margin: 0, color: "#F8FAFC", fontFamily: "'Inter', sans-serif", fontSize: ".82rem", fontWeight: 650 }}>1. Scan the QR code in your authenticator app.</p>
                <p style={{ margin: ".42rem 0 .7rem", color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>If scanning is unavailable, add an account manually with this setup key. It expires in five minutes.</p>
                <label htmlFor="totp-manual-key" style={{ display: "block", color: "#C4B5FD", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".68rem", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}>Manual setup key</label>
                <input id="totp-manual-key" readOnly value={totpEnrollment.manualKey} aria-label="Manual authenticator setup key" style={{ width: "100%", minHeight: 38, marginTop: ".35rem", padding: ".5rem .6rem", borderRadius: ".5rem", boxSizing: "border-box", color: "#E9D5FF", background: "rgba(9,13,25,.55)", border: "1px solid rgba(196,181,253,.25)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: ".72rem", letterSpacing: ".05em" }} />
              </div>
            </div>
            <div>
              <label htmlFor="totp-enrollment-code" style={{ display: "block", color: "#F8FAFC", fontFamily: "'Inter', sans-serif", fontSize: ".8rem", fontWeight: 650 }}>2. Enter the current six-digit code to confirm.</label>
              <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginTop: ".45rem" }}>
                <input id="totp-enrollment-code" value={totpCode} onChange={event => setTotpCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required placeholder="Enter the 6-digit code" style={{ flex: "1 1 150px", minHeight: 42, padding: ".6rem .75rem", borderRadius: ".55rem", color: "#FFF", background: "rgba(9,13,25,.55)", border: "1px solid rgba(255,255,255,.16)", fontFamily: "'Inter', sans-serif", fontSize: "1rem", fontWeight: 700, letterSpacing: ".22em" }} />
                <button type="submit" disabled={isSettingUpTotp} style={{ minHeight: 42, padding: ".6rem .85rem", border: 0, borderRadius: ".55rem", color: "#170A2A", background: isSettingUpTotp ? "rgba(196,181,253,.55)" : "linear-gradient(135deg,#E9D5FF,#C4B5FD)", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".76rem", fontWeight: 750, cursor: isSettingUpTotp ? "wait" : "pointer" }}>{confirmTotpEnrollment.isPending ? "Confirming…" : "Confirm app"}</button>
                <button type="button" disabled={isSettingUpTotp} onClick={() => { setTotpEnrollment(null); setTotpCode(""); }} style={{ minHeight: 42, padding: ".6rem .75rem", border: "1px solid rgba(255,255,255,.16)", borderRadius: ".55rem", color: "#CBD5E1", background: "rgba(255,255,255,.03)", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".76rem", fontWeight: 650, cursor: isSettingUpTotp ? "wait" : "pointer" }}>Cancel</button>
              </div>
            </div>
          </form>
        )}

        {recoveryCodes && !recoveryAcknowledged && (
          <div role="status" aria-live="polite" style={{ marginTop: "1rem", padding: "1rem", borderRadius: ".8rem", color: "#FDE68A", background: "rgba(245,166,35,.08)", border: "1px solid rgba(245,166,35,.3)" }}>
            <p style={{ margin: 0, fontFamily: "'Space Grotesk', sans-serif", fontSize: ".88rem", fontWeight: 750 }}>Save these recovery codes now.</p>
            <p style={{ margin: ".35rem 0 .75rem", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>Each code can be used once if your authenticator app is unavailable. These are shown only now; store them in a password manager or another secure location.</p>
            <div aria-label="One-time recovery codes" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(148px, 1fr))", gap: ".45rem", padding: ".7rem", borderRadius: ".6rem", color: "#FEF3C7", background: "rgba(9,13,25,.46)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: ".78rem", letterSpacing: ".05em" }}>
              {recoveryCodes.map(recoveryCode => <code key={recoveryCode}>{recoveryCode}</code>)}
            </div>
            <label style={{ display: "flex", alignItems: "flex-start", gap: ".55rem", marginTop: ".8rem", color: "#FDE68A", fontFamily: "'Inter', sans-serif", fontSize: ".77rem", lineHeight: 1.45, cursor: "pointer" }}>
              <input type="checkbox" checked={recoveryAcknowledged} onChange={event => setRecoveryAcknowledged(event.target.checked)} style={{ marginTop: 2, accentColor: "#F59E0B" }} />
              I have saved these recovery codes in a secure place.
            </label>
            <button type="button" disabled={!recoveryAcknowledged} onClick={() => setRecoveryCodes(null)} style={{ marginTop: ".72rem", minHeight: 38, padding: ".52rem .75rem", border: 0, borderRadius: ".5rem", color: recoveryAcknowledged ? "#21110B" : "#6B7280", background: recoveryAcknowledged ? "#FDE68A" : "rgba(255,255,255,.12)", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".74rem", fontWeight: 750, cursor: recoveryAcknowledged ? "pointer" : "not-allowed" }}>I saved my codes</button>
          </div>
        )}

        {totpStatus.data?.enabled && !recoveryCodes && (
          <div style={{ marginTop: "1rem", padding: ".9rem", borderRadius: ".75rem", background: "rgba(16,185,129,.07)", border: "1px solid rgba(16,185,129,.2)" }}>
            <p style={{ display: "flex", alignItems: "center", gap: ".42rem", margin: 0, color: "#A7F3D0", fontFamily: "'Inter', sans-serif", fontSize: ".8rem", fontWeight: 650 }}><ShieldCheck size={15} aria-hidden="true" /> Authenticator app is active · {totpStatus.data.remainingRecoveryCodes} recovery {totpStatus.data.remainingRecoveryCodes === 1 ? "code" : "codes"} remaining</p>
            <details style={{ marginTop: ".75rem" }}>
              <summary style={{ color: "#FDA4AF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".75rem", fontWeight: 700, cursor: "pointer" }}>Remove authenticator app</summary>
              <div style={{ display: "flex", gap: ".55rem", flexWrap: "wrap", marginTop: ".7rem" }}>
                <input value={removalCode} onChange={event => setRemovalCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="Enter the current 6-digit code" aria-label="Current authenticator code required to remove factor" style={{ flex: "1 1 180px", minHeight: 38, padding: ".5rem .65rem", borderRadius: ".5rem", color: "#FFF", background: "rgba(9,13,25,.55)", border: "1px solid rgba(251,113,133,.3)", fontFamily: "'Inter', sans-serif", fontSize: ".8rem" }} />
                <button type="button" onClick={() => void removeTotpFactor()} disabled={removeTotp.isPending} style={{ minHeight: 38, padding: ".5rem .7rem", borderRadius: ".5rem", color: "#FDA4AF", background: "rgba(244,63,94,.06)", border: "1px solid rgba(251,113,133,.35)", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".74rem", fontWeight: 700, cursor: removeTotp.isPending ? "wait" : "pointer" }}><Trash2 size={13} aria-hidden="true" /> Remove</button>
              </div>
              <p style={{ margin: ".48rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: ".72rem", lineHeight: 1.45 }}>A current authenticator code is required. This will permanently invalidate its remaining recovery codes.</p>
            </details>
          </div>
        )}
      </section>
    </section>
  );
}
